/**
 * @file: 层板中cube信息
 * @author: xiaolong.huang@dmall.com
 */

import * as CubeTree from '../../cubeTree/index';
import * as Toolkit from '../../toolkit/index';
import FrontCubeData from '../../frontCubeData/index';
import CodiSystem from '../../codiSystem/index';
import Utils from '../../utils/util';
import { containSubGoodsSizeByCubeId, hasFoldGoods } from '../goods/index';
import { textareaSize } from '../textarea/index';
import { getCubeNumberValue } from '../func/index';
import { layerCubeSize } from '../size/index';
import { layerLink } from '../../interaction/layerGroup/link';

const justifyContentEnum: FreeObjMap = {
    // 从左到右追加
    '8-1': '|||---',

    // 从右到左追加
    '8-2': '---|||',

    // 均匀分布
    '9-1': '-|-|-',
    '9-2': '-|-|-',

    // 贴边均匀分布
    '10-1': '|-|-|',
    '10-2': '|-|-|',

    // 手动自由放置
    '1-1': '|-|--|---',
    '1-2': '|-|--|---'
};

// 层板被占用的空间高度，算商品的最高尺寸
function layerSpaceHeightUsed(layerId: CubeId): PhysicsMeasurement {
    let childIds: Array<CubeId> = CubeTree.findChildCubeIds(layerId);
    let result: PhysicsMeasurement = 0;

    childIds.forEach((childId: CubeId): void => {
        result = Math.max(containSubGoodsSizeByCubeId(childId).height, result);
    });

    return result;
}

// 层板均分时，cube数目统计
function cubeCountInLayer(childCubeIds: CubeId[]): number {
    let result: number = 0;

    childCubeIds.forEach((cubeId: CubeId): void => {
        result += getCubeLayoutCount(cubeId);
    });

    return result;
}

// 隔板间 cube 数目
function cubesBetweenBafflePlate(target: CubeId): CubeId[] {
    let result: CubeId[] = [];
    let parent: CubeId = CubeTree.findParentIdByCubeId(target);
    let targetIndex: number = CubeTree.getCubeIndex(target);
    let leftIndex: number = targetIndex - 1;
    let rightIndex: number = targetIndex;
    let leftCube: CubeId|null = CubeTree.findChildCubeByIndex(parent, leftIndex);
    let rightCube: CubeId|null = CubeTree.findChildCubeByIndex(parent, rightIndex);

    while(leftCube && !CubeTree.isBafflePlate(leftCube)) {
        result.push(leftCube);
        leftIndex--;
        leftCube = CubeTree.findChildCubeByIndex(parent, leftIndex);
    }

    while(rightCube && !CubeTree.isBafflePlate(rightCube)) {
        result.push(rightCube);
        rightIndex++;
        rightCube = CubeTree.findChildCubeByIndex(parent, rightIndex);
    }

    return result;
}

function getCubeLayoutCount(target: CubeId): number {
    if (hasFoldGoods(target) || CubeTree.isTextarea(target)) {
        return 1;
    }

    let surfaceWidth: number = FrontCubeData.getNumberValue(target, 'surfaceWidth');

    return surfaceWidth;
}

// 获取层板布局类型，如果层板中有隔板参与布局，临时强制转为追加布局
function layoutType(layerId: CubeId): LayoutType {
    /**
     * xplaceType 布置X放置类型
     *
     * 1. 手动放置 1
     * 2. 追加 8
     * 3. 均匀分布 9
     * 4. 贴边均匀 10
     */
    let xplaceType: number = getCubeNumberValue(layerId, 'xplaceType');
    let isGrouped: boolean = layerIsGrouped(layerId);

    // 层板中存在隔板
    if (layerHasBafflePlate(layerId) && xplaceType !== 1
        || (isGrouped && layerGroupHasBafflePlate(layerId))) {
        xplaceType = 8;
    }

    /**
     * xdirection 布置X方向
     *
     * 1. 从左到右 1
     * 2. 从右到左 2
     */
    let xdirection: number = getCubeNumberValue(layerId, 'xdirection');
    let justify: string = `${xplaceType}-${xdirection}`;
    let result: LayoutType = justifyContentEnum[justify];

    return result;
}

function layerGroupHasBafflePlate(layer: CubeId): boolean {
    let link: LinkedListInstance|null = layerLink(layer);
    let groupLayers: CubeId[] = [];

    if (!link) {
        groupLayers = [layer];
    }
    else {
        groupLayers = link.valueList();
    }

    return groupLayers.some((groupLayer: CubeId): boolean => {
        return layerHasBafflePlate(groupLayer);
    });
}

function layerHasBafflePlate(layer: CubeId): boolean {
    let childIds: CubeId[] = CubeTree.findChildCubeIds(layer);

    for (let i = 0; i < childIds.length; i++) {
        if (CubeTree.isBafflePlate(childIds[i])) {
            return true;
        }
    }

    return false;
}

/**
 * 层板是否在相邻货架中
 *
 * 1. 判断层板所在不同的货架
 * 2. 判断货架是否相邻
 */
function layerInAdjoiningShelf(layers: CubeId[]): boolean {
    let shelfs: Set<CubeId> = new Set();

    if (layers.length < 2) {
        return false;
    }

    // 判断层板所在不同的货架
    for (let i = 0; i < layers.length; i++) {
        let parent: CubeId = CubeTree.findParentIdByCubeId(layers[i]);

        if (!CubeTree.isShelf(parent)) {
            return false;
        }

        if (shelfs.has(parent)) {
            return false;
        }

        shelfs.add(parent);
    }

    let shelfsSort: CubeId[] = CodiSystem.sortByLeft(Array.from(shelfs));

    // 判断货架是否相邻
    for (let i = 0; i < shelfsSort.length; i++) {
        if (i === shelfsSort.length - 1) {
            continue;
        }

        let nextShelf: CubeId = CubeTree.findNextCube(shelfsSort[i], []);

        if (shelfsSort[i + 1] !== nextShelf) {
            return false;
        }
    }

    return true;
}

// 层板是否顶端对齐
function layerIsTopAlign(layers: CubeId[]): boolean {
    let baseTop: LogicsicsMeasurement = 0;

    for (let i = 0; i < layers.length; i++) {
        let layerTop: LogicsicsMeasurement = CodiSystem.getLogicCoordinate(layers[i]).top;

        if (i === 0) {
            baseTop = layerTop;
            continue;
        }

        if (baseTop !== layerTop) {
            return false;
        }
    }

    return true;
}

// 层板是否碰撞
function layersIsTouch(layers: CubeId[]): boolean {
    if (layers.length <= 1) {
        return false;
    }

    for (let i = 0; i < layers.length; i++) {
        if (i === layers.length - 1) {
            continue;
        }
        let curLayer: LogicCoordinate = CodiSystem.getLogicCoordinate(layers[i]);
        let nextLayer: LogicCoordinate = CodiSystem.getLogicCoordinate(layers[i + 1]);
        let curRect: Rect = {
            width: curLayer.width,
            height: curLayer.height,
            left: curLayer.left,
            top: curLayer.top
        };
        let nextRect: Rect = {
            width: nextLayer.width,
            height: nextLayer.height,
            left: nextLayer.left,
            top: nextLayer.top
        };

        if (!Toolkit.beTouch(curRect, nextRect)) {
            return false;
        }
    }

    return true;
}

// 几个层板的宽度和
function layersTotalWidth(layers: CubeId[]): PhysicsMeasurement {
    return layers.reduce((total: PhysicsMeasurement, layer: CubeId): PhysicsMeasurement => {
        let moduleWidth: number = FrontCubeData.getNumberValue(layer, 'moduleWidth');

        return Utils.math(total, moduleWidth, '+');
    }, 0);
}

function layerCubesTotalWidth(cubeIds: CubeId[]): PhysicsMeasurement {
    return cubeIds.reduce((total: PhysicsMeasurement, cubeId: CubeId): PhysicsMeasurement => {
        let cubeWidth: PhysicsMeasurement = layerCubeSize(cubeId).width;

        return Utils.math(total, cubeWidth, '+');
    }, 0);
}

// 判断层板是否是编组状态
function layerIsGrouped(layer: CubeId): boolean {
    let grouped: number = FrontCubeData.getValue(layer, 'grouped');
    let link: LinkedListInstance|null = layerLink(layer);

    return grouped == 1 && Utils.isExist(link);
}

// todo: 中间链接层板不算左右延长
function layoutSpaceWidth(target: CubeId): PhysicsMeasurement {
    let result: PhysicsMeasurement = 0;
    let parent: CubeId = CubeTree.findParentIdByCubeId(target);
    let childIds: CubeId[] = CubeTree.findChildCubeIds(parent);
    let moduleWidth: PhysicsMeasurement = FrontCubeData.getNumberValue(parent, 'moduleWidth');
    let grouped: boolean = layerIsGrouped(parent);
    let targetIndex: number = CubeTree.getCubeIndex(target);
    let leftIndex: number = targetIndex - 1;
    let rightIndex: number = targetIndex;
    let leftCube: CubeId|null = CubeTree.findChildCubeByIndex(parent, leftIndex);
    let rightCube: CubeId|null = CubeTree.findChildCubeByIndex(parent, rightIndex);
    let extendLeft: PhysicsMeasurement = FrontCubeData.getNumberValue(parent, 'extendLeft');
    let extendRight: PhysicsMeasurement = FrontCubeData.getNumberValue(parent, 'extendRight');
    let leftLine: PhysicsMeasurement = 0;
    let rightLine: PhysicsMeasurement = 0;

    while(leftCube && !CubeTree.isBafflePlate(leftCube)) {
        leftIndex--;
        leftCube = CubeTree.findChildCubeByIndex(parent, leftIndex);
    }

    while(rightCube && !CubeTree.isBafflePlate(rightCube)) {
        rightIndex++;
        rightCube = CubeTree.findChildCubeByIndex(parent, rightIndex);
    }

    // 左边界
    if (leftIndex < 0) {
        leftLine = -extendLeft;
    }
    else {
        let posX: PhysicsMeasurement = FrontCubeData.getNumberValue(leftCube, 'posX');
        let width: PhysicsMeasurement = FrontCubeData.getNumberValue(leftCube, 'width');

        leftLine = Utils.math(posX, width, '+');
    }

    // 右边界
    if (rightIndex >= childIds.length) {
        rightLine = Utils.math(moduleWidth, extendRight, '+');
    }
    else {
        let posX: PhysicsMeasurement = FrontCubeData.getNumberValue(rightCube, 'posX');

        rightLine = posX;
    }

    return Utils.math(rightLine, leftLine, '-');
}

export {
    layerSpaceHeightUsed,
    cubeCountInLayer,
    layoutType,
    layerInAdjoiningShelf,
    layerIsTopAlign,
    layersIsTouch,
    layersTotalWidth,
    layerCubesTotalWidth,
    layerIsGrouped,
    cubesBetweenBafflePlate,
    layoutSpaceWidth
};
